import { formatThousandsComma } from '@noor/lib';
import { env } from '@noor/config';

/**
 * Presentational formatters for legacy screens. Note these intentionally do
 * NOT use `formatThousands` from `@noor/lib`: that helper groups with `ru-RU`
 * thin spaces (`1 001`), while the old panel inserted ASCII commas (`1,001`)
 * regardless of locale. The live panel renders "1,001 sum", so legacy mode
 * must too — hence `formatThousandsComma`.
 */

/**
 * Group a number with commas, rounding to a whole unit — the old
 * `thousandDivider`'s behavior. Re-exported under the legacy name so legacy
 * call sites read consistently; the implementation lives in `@noor/lib`
 * because more than one legacy entity needs it and slices may not import each
 * other.
 */
export const legacyThousands = formatThousandsComma;

/**
 * The list's date column: `DD.MM.YYYY HH:mm`. Built from explicit parts rather
 * than `Intl` so the output can't shift with the app's locale — the old panel
 * used a fixed dayjs format and the column is compared against the backend's
 * own timestamps.
 */
export function legacyDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Resolve a storage-relative path into an absolute file URL — the old
 * `getImageUrl`, quirks included: absolute/blob URLs pass through, the base is
 * joined with a `/`, and a value without any slash yields `null` (the old
 * helper returned `undefined` there, which rendered nothing).
 */
export function legacyFileUrl(path: string | null | undefined): string | null {
  if (!path || path.split('/').length <= 1) return null;
  if (
    path.startsWith('https://') ||
    path.startsWith('http://') ||
    path.startsWith('blob:')
  )
    return path;
  return `${env.VITE_FILE_API_URL}/${path}`;
}

/** `YYYY-MM-DD` — the wire format the legacy list endpoint's date range uses. */
export function legacyDateParam(d: Date): string {
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The list's date column, exactly as the old grid printed it:
 * `HH:mm, DD.MM.YYYY` — time FIRST, then the date.
 */
export function legacyListDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}, ${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/**
 * The drawer title's timestamp: `DD MMMM HH:mm` (`26 августа 14:30`), with a
 * `-` fallback for an invalid date — both from the old `OrderDrawer`.
 *
 * Built with `formatToParts` on the COMBINED day+month format so Russian gets
 * the genitive month ("августа", not "август") — a standalone month format
 * yields the nominative case.
 */
export function legacyDayMonthTime(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const parts = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
  }).formatToParts(d);
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${day} ${month} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Courier name as the old grid abbreviated it: `Ivan. P` — first name, a
 * literal dot, then the last-name INITIAL. Null (no courier) returns null so
 * the cell can render the "не определен" fallback instead.
 */
export function legacyCourierName(
  courier: { first_name: string | null; last_name: string | null } | null,
): string | null {
  if (!courier?.first_name) return null;
  const initial = courier.last_name ? courier.last_name[0] : '';
  return `${courier.first_name}. ${initial}`;
}

/**
 * The old `formatPhoneNumber`: masks a `+998…` number as
 * `+998 90 123 45 67` (2-3-2-2 groups). Any unrecognized prefix falls back to
 * the UZ mask — the old helper did the same — and an empty value yields `''`.
 */
export function legacyFormatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/^\+/, '');
  // Group patterns per dial code, transcribed from the old country table.
  const masks: { prefix: string; groups: number[] }[] = [
    { prefix: '998', groups: [3, 2, 3, 2, 2] },
    { prefix: '77', groups: [1, 3, 3, 2, 2] },
    { prefix: '995', groups: [3, 3, 3, 3] },
    { prefix: '996', groups: [3, 3, 3, 3] },
    { prefix: '992', groups: [3, 2, 3, 2, 2] },
  ];
  const mask = masks.find((m) => digits.startsWith(m.prefix)) ?? masks[0]!;
  const out: string[] = [];
  let i = 0;
  for (const len of mask.groups) {
    if (i >= digits.length) break;
    out.push(digits.slice(i, i + len));
    i += len;
  }
  if (i < digits.length) out.push(digits.slice(i));
  return `+${out.join(' ')}`;
}
